"use client";

import type {
  LeadFormFieldChangeHandler,
  TextStandardInput,
} from "./form-types";
import type { StandardInputErrors } from "./standard-input-config";

import { formatPartialPhoneNumber } from "@homeinstead/shared";
import { cn } from "@homeinstead/ui";
import { Input } from "@homeinstead/ui/components/Input";
import { useId } from "react";
import { LeadFormErrorInline } from "./LeadFormErrorInline";
import { resolveLabel, resolvePlaceholder } from "./standard-input-config";
import { usePersistedFieldState } from "./use-persisted-field-state";

interface PhoneInputFieldProps {
  input: TextStandardInput;
  errors: StandardInputErrors;
  onFieldChange?: LeadFormFieldChangeHandler;
}

export function PhoneInputField({
  input,
  errors,
  onFieldChange,
}: PhoneInputFieldProps) {
  // Formatted, not raw digits: PersistedFieldInputs renders a hidden "phone"
  // input before this one, and FormData.get("phone") returns the first match —
  // the submitted value must stay byte-identical to what the live input shows.
  const [displayValue, setDisplayValue] = usePersistedFieldState(
    "phone",
    onFieldChange,
  );
  const placeholder = resolvePlaceholder(input);
  const fieldErrors = errors?.phone ?? [];
  const hasError = fieldErrors.length > 0;
  const uid = useId();
  const errorId = `${uid}-error`;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Pass raw digits to avoid re-formatting already-formatted characters (backspace fix)
    const raw = e.target.value.replace(/\D/g, "");
    const formatted = formatPartialPhoneNumber({ franchisePhone: raw });
    setDisplayValue(formatted);
  };

  return (
    <div className="flex flex-col gap-1">
      <label className="sr-only" htmlFor={uid}>
        {resolveLabel(input)}
      </label>
      <Input
        aria-describedby={hasError ? errorId : undefined}
        aria-invalid={hasError || undefined}
        autoComplete="tel"
        className={cn({ "border-red-500": hasError })}
        id={uid}
        name="phone"
        onChange={handleChange}
        placeholder={placeholder}
        type="tel"
        value={displayValue}
      />
      {hasError ? (
        <LeadFormErrorInline id={errorId}>{fieldErrors[0]}</LeadFormErrorInline>
      ) : null}
    </div>
  );
}
